/**
 * Appointment Data Atoms
 *
 * Atoms for managing appointment context during voice note sessions.
 */
import { atom } from 'jotai';
import { isEqual } from 'lodash';

import type { VideaNotesAppointmentData } from '../types';

import { mediaBlobUrlAtom, stoppedRecordingDurationAtom } from './recording';

// ============================================================================
// Base Atoms
// ============================================================================

/** Current appointment data for the voice note session */
export const appointmentDataAtom = atom<VideaNotesAppointmentData | null>(null);

/** Currently selected note ID (persists across sheet open/close) */
export const selectedNoteIdAtom = atom<string | undefined>(undefined);

// ============================================================================
// Action Atoms
// ============================================================================

/**
 * Writable atom that updates appointment data and resets
 * appointment-specific recording state when appointment changes.
 */
export const appointmentDataWithResetAtom = atom(
  (get) => get(appointmentDataAtom),
  (get, set, newData: VideaNotesAppointmentData | null) => {
    const prevData = get(appointmentDataAtom);

    // Check if the data has actually changed using deep equality
    const isDataChanged = !isEqual(prevData, newData);

    if (isDataChanged) {
      set(appointmentDataAtom, newData);
      set(mediaBlobUrlAtom, undefined);
      set(stoppedRecordingDurationAtom, 0);
      set(selectedNoteIdAtom, undefined);
    }
  },
);
